import { memo, useCallback, useEffect, useRef, useState } from 'react';

import type { ApiError } from '@/api/client.types';
import { Card } from '@/components/Card/Card';
import { useKuduAIHomeworkService } from '@/features/ai-homework/aiHomeworkService';
import type { QuestionAIState } from '@/features/ai-homework/aiHomeworkTypes';
import { useAIHintVisibility } from '@/features/ai-homework/useAIHintVisibility';
import { useQuestionAI } from '@/features/ai-homework/useQuestionAI';
import { useQuestionInteractionState } from '@/features/content/hooks/useQuestionInteractionState';
import { QuestionStatsDisplay } from '@/features/content/stats/QuestionStatsDisplay';
import type { UserRole } from '@/features/courses/useCourse';
import { useOnlineStatus } from '@/pwa/useOnlineStatus';
import {
  getAnswerSubmissionStatusLabel,
  useSyncStatus,
  type AnswerSubmissionStatus,
} from '@/pwa/useSyncStatus';

import { AIUploadInstructorView } from './aiUpload/AIUploadInstructorView';
import { AIUploadStudentView } from './aiUpload/AIUploadStudentView';
import { AttemptTracker } from './AttemptTracker';
import { ExactAnswerQuestion } from './ExactAnswerQuestion';
import { HintTabContent } from './HintTabContent';
import { MultiPartQuestion } from './MultiPartQuestion';
import { MultipleChoiceQuestion } from './MultipleChoiceQuestion';
import { NumericalQuestion } from './NumericalQuestion';
import { QuestionContent } from './QuestionContent';
import { SelectAllQuestion } from './SelectAllQuestion';
import {
  QuestionInfoPanel,
} from './QuestionInfoPanel';
import type { QuestionInfoPanelTabId } from './InfoPanelTabBar';
import type {
  NormalizedQuestionData,
  NormalizedQuestionSectionData,
} from './questionNormalizer';
import { PracticeTabContent } from './PracticeTabContent';
import { SolutionTabContent } from './SolutionTabContent';
import { useInfoPanelTabs } from './useInfoPanelTabs';
import {
  getSubmissionErrorMessage,
  useQuestionSubmission,
  type ExistingQuestionStat,
  type NormalizedAnswerInput,
  type QuestionSubmissionSectionContext,
  type QuestionSubmissionSectionKind,
  type RandomAnswerData,
} from './useQuestionSubmission';
import styles from './QuestionCard.module.css';

interface QuestionCardProps {
  aiContext?: {
    chapterTitle: string;
    courseSubject?: string | null;
    sectionKind: 'homework' | 'preLecture';
  };
  areSolutionsVisible: boolean;
  /** When the section closes for this viewer (see NormalizedQuestionSectionData). */
  closesAtUtcMilli?: number;
  componentId: string;
  courseId: string;
  headingLevel?: 3 | 4;
  hideStats?: boolean;
  isAssigned?: boolean;
  isSectionClosed: boolean;
  isSectionOpen: boolean;
  onAnnounce?(message: string): void;
  /** Called when a submission is refused because the section has closed. */
  onClosed?(): void;
  /** Called when the server, not this page, refuses it for that. */
  onRefusedAfterClose?(): void;
  question: NormalizedQuestionData;
  role: UserRole;
  submissionContext: QuestionSubmissionSectionContext;
}

function getPointsLabel(pointsPossible: number) {
  return `${pointsPossible} point${pointsPossible === 1 ? '' : 's'}`;
}

// How long a check's verdict may take to reach the page (it comes with the
// chapter's refetch) before the card says only that it was checked.
const VERDICT_WAIT_MS = 4000;

const verdictWords: Partial<
  Record<NonNullable<NormalizedQuestionData['feedback']>['status'], string>
> = {
  correct: 'correct',
  incorrect: 'incorrect',
  partial: 'partly correct',
  participation: 'participation credit',
};

/**
 * What a student is told when a section closes on an open page: "This homework
 * closed on Oct 24, 2026, 11:59 PM." in their own locale. Without a close time
 * that has passed (the instructor closed it early), there is no time to give.
 */
export function getSectionClosedMessage(
  closesAtUtcMilli: number | undefined,
  sectionKind: QuestionSubmissionSectionKind | undefined,
) {
  const isPreLecture = sectionKind === 'preLecture';
  const section = isPreLecture ? 'These pre-lecture questions' : 'This homework';

  if (closesAtUtcMilli === undefined || closesAtUtcMilli > Date.now()) {
    return `${section} ${isPreLecture ? 'are' : 'is'} closed.`;
  }

  const closedAt = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(closesAtUtcMilli));

  return `${section} closed on ${closedAt}.`;
}

// What a student is told of a submission the section's close stopped: by this page's
// clock, so that nothing is sent, or by the server's refusal (issue #44), which also
// catches a request on its way as the due date passes, a clock behind the server's,
// and what the service worker kept offline.
function getClosedSubmissionMessage(
  closesAtUtcMilli: number | undefined,
  mode: 'check' | 'save',
  sectionKind: QuestionSubmissionSectionKind | undefined,
) {
  return `${getSectionClosedMessage(closesAtUtcMilli, sectionKind)} Your answer was not ${
    mode === 'check' ? 'checked' : 'saved'
  }.`;
}

// The page draws its own words for this. The server's are for a page from before it.
function isSectionClosedRefusal(error: unknown) {
  return (error as Partial<ApiError> | null | undefined)?.details?.code === 'section_closed';
}

// What the question is told when the close stopped a submission that was never kept,
// by the server's word or this page's clock. Nothing stored changes, so nothing else
// tells a box that can no longer change to show what is stored.
const STOPPED_BY_THE_CLOSE = 'stopped-by-the-close';

// Offline, a check is not sent. Its whole point is the verdict, and kept for
// later it would use an attempt with the student not there to see the result:
// someone working on a plane could spend every attempt blind (issue #143). An
// upload has no Save to offer.
function getOfflineCheckMessage(
  questionKind: NormalizedQuestionData['kind'],
  isEntryUnsaved: boolean,
) {
  if (questionKind === 'aiUpload') {
    return "You're offline, so your file was not submitted. Submit it when you're back online.";
  }

  return isEntryUnsaved
    ? "You're offline, so your answer was not checked. Save it to keep it, and check it when you're back online."
    : "You're offline, so your answer was not checked. Check it when you're back online.";
}

// The browser said it was online and the network was dead, so the page could
// not tell, and the service worker kept the check. It uses an attempt when it
// is sent.
function getKeptCheckMessage(questionKind: NormalizedQuestionData['kind']) {
  return questionKind === 'aiUpload'
    ? 'Your connection dropped. Your file is kept on this device and will be submitted when it is sent.'
    : 'Your connection dropped. Your answer is kept on this device and will be checked when it is sent.';
}

const emptyHintState: QuestionAIState['hint'] = {
  error: null,
  isLoading: false,
  isRegenerating: false,
  response: null,
  statusMessage: null,
  vote: {
    error: null,
    isVoting: false,
    userVote: null,
  },
};

function QuestionAIInfoPanel({
  aiContext,
  componentId,
  hintDisclosure,
  hintsHidden,
  isSectionAvailable,
  question,
  role,
  solutionDisclosure,
}: {
  aiContext: NonNullable<QuestionCardProps['aiContext']>;
  componentId: string;
  hintDisclosure?: NormalizedQuestionData['hint'];
  hintsHidden: boolean;
  isSectionAvailable: boolean;
  question: NormalizedQuestionData;
  role: UserRole;
  solutionDisclosure?: NormalizedQuestionData['solution'];
}) {
  const aiHomeworkService = useKuduAIHomeworkService();
  const questionAI = useQuestionAI({
    aiHomeworkService,
    chapterTitle: aiContext.chapterTitle,
    componentId,
    course: {
      parentSubject: aiContext.courseSubject ?? undefined,
    },
    isHintEnabled: question.allowAIHints === true,
    isPracticeEnabled: question.allowAIPracticeProblems === true,
    question,
  });
  const aiVisibility = useAIHintVisibility({
    hasLoadedHint:
      questionAI.hint.response !== null ||
      questionAI.hint.isRegenerating ||
      questionAI.hint.statusMessage !== null,
    isSectionAvailable,
    question,
    role,
    sectionKind: aiContext.sectionKind,
  });
  const infoPanelTabs = useInfoPanelTabs({
    aiState: questionAI,
    aiVisibility,
    hintDisclosure,
    hintsHidden,
    solutionDisclosure,
  });

  function renderInfoPanelContent(tabId: QuestionInfoPanelTabId) {
    if (tabId === 'hint') {
      return (
        <HintTabContent
          aiHint={questionAI.hint}
          builtInHint={hintDisclosure}
          onRequestAIHint={() => {
            void questionAI.requestHint();
          }}
          onVoteAIHint={(vote) => {
            void questionAI.voteOnHint(vote);
          }}
          showAIHintButton={aiVisibility.showHintButton}
        />
      );
    }

    if (tabId === 'practice') {
      return (
        <PracticeTabContent
          onRequestAnotherPractice={() => {
            void questionAI.requestAnotherPractice();
          }}
          onRequestPractice={() => {
            void questionAI.requestPractice();
          }}
          onSelectAnswer={questionAI.selectPracticeAnswer}
          onVotePractice={(vote) => {
            void questionAI.voteOnPractice(vote);
          }}
          practice={questionAI.practice}
          showPracticeButton={aiVisibility.showPracticeButton}
        />
      );
    }

    return <SolutionTabContent solution={solutionDisclosure} />;
  }

  if (infoPanelTabs.length === 0) {
    return null;
  }

  return (
    <QuestionInfoPanel
      renderContent={renderInfoPanelContent}
      tabs={infoPanelTabs}
    />
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
}

function normalizeNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeIdentifier(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  return normalizeString(value);
}

function getNestedQuestionStats(questionStat: ExistingQuestionStat) {
  const nested = questionStat.question_set_stats ?? questionStat.questionSetStats;

  if (!nested || !Array.isArray(nested.question_stats ?? nested.questionStats)) {
    return [];
  }

  return ((nested.question_stats ?? nested.questionStats) as ExistingQuestionStat[])
    .filter(isRecord);
}

// Builds an id -> stat lookup for a list of question stats.
//
// `deep` controls whether nested (question-set) stats are folded into the SAME flat
// map. Question ids are NOT globally unique: a multi-part question's first sub-part
// can carry the same backend id as its parent. Folding nested stats into the parent
// list's map therefore lets a sub-part overwrite (or be overwritten by) its parent
// under a shared id. So:
//   - buildOrderedQuestionStats walks the question TREE and looks up one level at a
//     time; it must use a SHALLOW map (`deep: false`) so a parent lookup can never
//     return one of its own sub-parts (which would drop the real nested stats and
//     reset every part's attempts).
//   - collectRandomAnswers walks into the parts itself and only ever looks up LEAF
//     questions, so it needs the DEEP map (`deep: true`) to find nested leaves.
function indexQuestionStats(
  questionStats: ExistingQuestionStat[],
  indexedStats: Record<string, ExistingQuestionStat> = {},
  { deep = true }: { deep?: boolean } = {},
) {
  questionStats.forEach((questionStat) => {
    const questionId = normalizeIdentifier(questionStat.id);

    if (questionId) {
      indexedStats[questionId] = questionStat;
    }

    if (!deep) {
      return;
    }

    const nestedStats = getNestedQuestionStats(questionStat);

    if (nestedStats.length > 0) {
      indexQuestionStats(nestedStats, indexedStats, { deep });
    }
  });

  return indexedStats;
}

function getRandomAnswerDataFromExistingStat(
  questionStat: ExistingQuestionStat | undefined,
): RandomAnswerData {
  const answer = isRecord(questionStat?.answer) ? questionStat.answer : null;

  if (!answer) {
    return {};
  }

  const hasCurrentRandomNumericalAnswerExtraExponent =
    'current_random_numerical_answer_extra_exponent' in answer ||
    'currentRandomNumericalAnswerExtraExponent' in answer;

  return {
    currentRandomExactAnswer:
      normalizeString(
        answer.current_random_exact_answer ?? answer.currentRandomExactAnswer,
      ) ?? undefined,
    currentRandomNumericalAnswer: normalizeNumber(
      answer.current_random_numerical_answer ??
        answer.currentRandomNumericalAnswer,
    ),
    currentRandomNumericalAnswerExtraExponent:
      answer.current_random_numerical_answer_extra_exponent === null ||
      answer.currentRandomNumericalAnswerExtraExponent === null
        ? null
        : normalizeNumber(
            answer.current_random_numerical_answer_extra_exponent ??
              answer.currentRandomNumericalAnswerExtraExponent,
          ),
    hasCurrentRandomNumericalAnswerExtraExponent,
    isRandomized:
      answer.current_random_exact_answer !== undefined ||
      answer.currentRandomExactAnswer !== undefined ||
      answer.current_random_numerical_answer !== undefined ||
      answer.currentRandomNumericalAnswer !== undefined,
  };
}

function buildDefaultAnswerForQuestion(
  question: NormalizedQuestionData,
  sectionKind: QuestionSubmissionSectionKind,
): Record<string, unknown> {
  let randomizedPayload: Record<string, unknown> = {};

  if (question.usesRandomVariables) {
    if (question.kind === 'exactAnswer') {
      randomizedPayload = question.currentRandomExactAnswer !== undefined
        ? {
            current_random_exact_answer: question.currentRandomExactAnswer,
          }
        : {};
    } else if (
      question.kind !== 'multiPart' &&
      question.kind !== 'aiUpload' &&
      // A select-all answer is a set of chosen indices, never a computed value,
      // so there is no randomized numerical answer to carry.
      question.kind !== 'selectAll' &&
      question.currentRandomNumericalAnswer !== undefined
    ) {
      randomizedPayload = {
        current_random_numerical_answer: question.currentRandomNumericalAnswer,
        current_random_numerical_answer_extra_exponent:
          question.currentRandomNumericalAnswerExtraExponent ?? null,
      };
    } else if (
      question.kind === 'multipleChoice' &&
      question.currentRandomExactAnswer !== undefined
    ) {
      randomizedPayload = {
        current_random_exact_answer: question.currentRandomExactAnswer,
      };
    }
  }

  return sectionKind === 'homework' && question.kind === 'multipleChoice'
    ? {
        answer_index: [-1],
        ...randomizedPayload,
      }
    : randomizedPayload;
}

// The stored entry for the question at `position`, when none carries the
// question's name.
//
// An entry is named after its question: the question's id, or its uid when it
// has no id. Both can change under a student mid-assignment (a question saved
// by someone other than its author is stored under a new uid), and then no
// entry carries the name. The page still shows the student the entry at the
// question's position, and the server merges what is sent with the entry at
// that position. Starting a submission from a blank one instead sent
// "attempts: 0", which the server takes as the question's count: the first
// answer a student saved after an instructor's edit set their attempts back to
// zero.
//
// Not when the entry there is named after another question on the page: the
// questions were put in another order, and it is that question's.
function getStoredStatAtPosition(
  storedQuestionStats: ExistingQuestionStat[],
  position: number,
  question: NormalizedQuestionData,
  questionIds: Set<string>,
): ExistingQuestionStat | undefined {
  const storedQuestionStat = storedQuestionStats[position];

  if (!isRecord(storedQuestionStat)) {
    return undefined;
  }

  const storedId = normalizeIdentifier(storedQuestionStat.id);

  if (storedId && questionIds.has(storedId)) {
    return undefined;
  }

  return { ...storedQuestionStat, id: question.id };
}

function buildOrderedQuestionStats(
  questions: NormalizedQuestionData[],
  indexedQuestionStats: Record<string, ExistingQuestionStat>,
  sectionKind: QuestionSubmissionSectionKind,
  storedQuestionStats: ExistingQuestionStat[] = [],
): ExistingQuestionStat[] {
  const questionIds = new Set(questions.map((question) => question.id));

  return questions.map((question, position) => {
    const existingQuestionStat =
      indexedQuestionStats[question.id] ??
      getStoredStatAtPosition(storedQuestionStats, position, question, questionIds);

    if (question.kind === 'multiPart') {
      // Shallow: a sub-part may share the parent's id, so only this parent's direct
      // children must be indexed here (see indexQuestionStats).
      const nestedQuestionStats = existingQuestionStat
        ? getNestedQuestionStats(existingQuestionStat)
        : [];
      const nestedIndexedQuestionStats = indexQuestionStats(
        nestedQuestionStats,
        {},
        { deep: false },
      );
      const questionSetStats = buildOrderedQuestionStats(
        question.parts,
        nestedIndexedQuestionStats,
        sectionKind,
        nestedQuestionStats,
      );

      return {
        ...existingQuestionStat,
        answer: isRecord(existingQuestionStat?.answer)
          ? existingQuestionStat.answer
          : {},
        id: question.id,
        question_set_stats: {
          question_stats: questionSetStats,
        },
        score: normalizeNumber(existingQuestionStat?.score) ?? 0,
      } satisfies ExistingQuestionStat;
    }

    if (existingQuestionStat) {
      return existingQuestionStat;
    }

    return {
      answer: buildDefaultAnswerForQuestion(question, sectionKind),
      id: question.id,
      score: 0,
    } satisfies ExistingQuestionStat;
  });
}

function collectRandomAnswers(
  questions: NormalizedQuestionData[],
  indexedQuestionStats: Record<string, ExistingQuestionStat>,
  randomAnswers: Record<string, RandomAnswerData> = {},
) {
  questions.forEach((question) => {
    if (question.kind === 'multiPart') {
      collectRandomAnswers(question.parts, indexedQuestionStats, randomAnswers);
      return;
    }

    if (question.kind === 'aiUpload') {
      return;
    }

    if (!question.usesRandomVariables) {
      return;
    }

    // A select-all answer is a set of chosen option indices. Even when the
    // question's options are built from random variables, there is no computed
    // answer value to persist alongside them — the correct indices do not move.
    if (question.kind === 'selectAll') {
      return;
    }

    const existingRandomAnswer = getRandomAnswerDataFromExistingStat(
      indexedQuestionStats[question.id],
    );
    const nextRandomAnswer: RandomAnswerData =
      question.kind === 'multipleChoice'
        ? {
            ...existingRandomAnswer,
            ...(question.currentRandomExactAnswer !== undefined
              ? {
                  currentRandomExactAnswer: question.currentRandomExactAnswer,
                }
              : {}),
            ...(question.currentRandomNumericalAnswer !== undefined
              ? {
                  currentRandomNumericalAnswer:
                    question.currentRandomNumericalAnswer,
                }
              : {}),
            ...(question.currentRandomNumericalAnswerExtraExponent !== undefined
              ? {
                  currentRandomNumericalAnswerExtraExponent:
                    question.currentRandomNumericalAnswerExtraExponent,
                  hasCurrentRandomNumericalAnswerExtraExponent: true,
                }
              : {}),
            isRandomized: true,
          }
        : question.kind === 'numerical'
          ? {
              ...existingRandomAnswer,
              ...(question.currentRandomNumericalAnswer !== undefined
                ? {
                    currentRandomNumericalAnswer:
                      question.currentRandomNumericalAnswer,
                  }
                : {}),
              ...(question.currentRandomNumericalAnswerExtraExponent !== undefined
                ? {
                    currentRandomNumericalAnswerExtraExponent:
                      question.currentRandomNumericalAnswerExtraExponent,
                    hasCurrentRandomNumericalAnswerExtraExponent: true,
                  }
                : {}),
              // Persist the variable values that produce this answer so display
              // and grading stay a matched pair (see resolveNumericalRandomAnswer).
              ...(question.currentRandomVariables !== undefined
                ? { variables: question.currentRandomVariables }
                : {}),
              isRandomized: true,
            }
          : {
              ...existingRandomAnswer,
              ...(question.currentRandomExactAnswer !== undefined
                ? {
                    currentRandomExactAnswer: question.currentRandomExactAnswer,
                  }
                : {}),
              isRandomized: true,
            };

    if (
      nextRandomAnswer.currentRandomExactAnswer !== undefined ||
      nextRandomAnswer.currentRandomNumericalAnswer !== undefined ||
      nextRandomAnswer.currentRandomNumericalAnswerExtraExponent !== undefined
    ) {
      randomAnswers[question.id] = nextRandomAnswer;
    }
  });

  return randomAnswers;
}

function collectRandomizedQuestionIds(
  questions: NormalizedQuestionData[],
  randomizedQuestionIds: Record<string, boolean> = {},
) {
  questions.forEach((question) => {
    if (question.kind === 'multiPart') {
      collectRandomizedQuestionIds(question.parts, randomizedQuestionIds);
      return;
    }

    randomizedQuestionIds[question.id] = Boolean(question.usesRandomVariables);
  });

  return randomizedQuestionIds;
}

export function buildQuestionSubmissionContext(
  section: NormalizedQuestionSectionData,
  relatedSections?: {
    homeworkSection?: NormalizedQuestionSectionData | null;
    preLectureSection?: NormalizedQuestionSectionData | null;
  },
): QuestionSubmissionSectionContext {
  const homeworkSection = relatedSections?.homeworkSection ?? (
    section.kind === 'homework' ? section : null
  );
  const preLectureSection = relatedSections?.preLectureSection ?? (
    section.kind === 'preLecture' ? section : null
  );
  // Deep maps (parent + nested folded together) are for collectRandomAnswers, which
  // only looks up leaf questions. buildOrderedQuestionStats instead walks the tree
  // level by level and must use a SHALLOW map so a parent lookup can't return one of
  // its own sub-parts under a shared id (see indexQuestionStats).
  const homeworkIndexedQuestionStats = indexQuestionStats(
    homeworkSection?.rawQuestionStats ?? [],
  );
  const preLectureIndexedQuestionStats = indexQuestionStats(
    preLectureSection?.rawQuestionStats ?? [],
  );
  const homeworkTopLevelQuestionStats = indexQuestionStats(
    homeworkSection?.rawQuestionStats ?? [],
    {},
    { deep: false },
  );
  const preLectureTopLevelQuestionStats = indexQuestionStats(
    preLectureSection?.rawQuestionStats ?? [],
    {},
    { deep: false },
  );
  const homeworkQuestionStats = homeworkSection
    ? buildOrderedQuestionStats(
        homeworkSection.questions,
        homeworkTopLevelQuestionStats,
        'homework',
        homeworkSection.rawQuestionStats,
      )
    : [];
  const preLectureQuestionStats = preLectureSection
    ? buildOrderedQuestionStats(
        preLectureSection.questions,
        preLectureTopLevelQuestionStats,
        'preLecture',
        preLectureSection.rawQuestionStats,
      )
    : [];
  const allQuestionStats =
    section.kind === 'preLecture' ? preLectureQuestionStats : homeworkQuestionStats;

  return {
    allQuestionStats,
    hasExistingSectionStats: section.rawQuestionStats.length > 0,
    homeworkHasExistingQuestionStats:
      (homeworkSection?.rawQuestionStats.length ?? 0) > 0,
    homeworkNumAttempts: homeworkSection?.sectionNumAttempts ?? 0,
    homeworkQuestionStats,
    homeworkTotalScore: homeworkSection?.sectionTotalScore ?? 0,
    preLectureHasExistingQuestionStats:
      (preLectureSection?.rawQuestionStats.length ?? 0) > 0,
    preLectureNumAttempts: preLectureSection?.sectionNumAttempts ?? 0,
    preLectureQuestionStats,
    preLectureTotalScore: preLectureSection?.sectionTotalScore ?? 0,
    randomizedQuestionIds: collectRandomizedQuestionIds(
      preLectureSection?.questions ?? [],
      collectRandomizedQuestionIds(homeworkSection?.questions ?? []),
    ),
    randomAnswers: collectRandomAnswers(
      preLectureSection?.questions ?? [],
      preLectureIndexedQuestionStats,
      collectRandomAnswers(
        homeworkSection?.questions ?? [],
        homeworkIndexedQuestionStats,
      ),
    ),
    sectionKind: section.kind,
    sectionNumAttempts: section.sectionNumAttempts,
    sectionStatsId: section.sectionStatsId,
    sectionTotalScore: section.sectionTotalScore,
  };
}

export const QuestionCard = memo(function QuestionCard({
  aiContext,
  areSolutionsVisible,
  closesAtUtcMilli,
  componentId,
  courseId,
  headingLevel = 3,
  hideStats = false,
  isAssigned = true,
  isSectionClosed,
  isSectionOpen,
  onAnnounce,
  onClosed,
  onRefusedAfterClose,
  question,
  role,
  submissionContext,
}: QuestionCardProps) {
  const questionSubmission = useQuestionSubmission();
  const { isOffline } = useOnlineStatus();
  const [lastSubmission, setLastSubmission] = useState<{
    attemptsBefore: number;
    mode: 'check' | 'save';
    queued: boolean;
    requestId: string;
  } | null>(null);
  // The question says when the answer on screen is not the one stored, so the
  // status line never calls a new choice saved (issue #92, HW-14).
  const [isEntryUnsaved, setIsEntryUnsaved] = useState(false);
  const [closedNotice, setClosedNotice] = useState<string | null>(null);
  const [offlineNotice, setOfflineNotice] = useState<string | null>(null);
  // The attempt count a check started from, while its verdict is awaited.
  const [awaitedVerdictFrom, setAwaitedVerdictFrom] = useState<number | null>(
    null,
  );
  const syncStatus = useSyncStatus(lastSubmission?.requestId);
  const HeadingTag = `h${headingLevel}` as const;
  const submissionPending = isAssigned ? questionSubmission.isPending : false;
  // A check the service worker kept, until it reports what became of it. Save
  // and Check wait with it: a second press would use a second attempt.
  const isCheckKept =
    lastSubmission?.queued === true &&
    lastSubmission.mode === 'check' &&
    syncStatus.status !== 'saved' &&
    syncStatus.status !== 'failed-to-sync';
  // Once it has gone out, it is a check like any other: its verdict comes with the
  // chapter's refetch, which PwaShell starts on the same report. Until then a second
  // press would use another attempt, with no verdict yet to go by.
  const isKeptCheckSent =
    lastSubmission?.queued === true &&
    lastSubmission.mode === 'check' &&
    syncStatus.status === 'saved';
  // A submission the service worker kept, and the server has since refused. The
  // question recorded it when the worker kept it, and has to hear of this.
  const refusedRequestId =
    syncStatus.status === 'failed-to-sync' ? lastSubmission?.requestId : undefined;
  const refusedSubmissionId =
    refusedRequestId ?? (closedNotice ? STOPPED_BY_THE_CLOSE : undefined);
  const isAwaitingSubmission =
    submissionPending ||
    isCheckKept ||
    (isKeptCheckSent && awaitedVerdictFrom !== null);
  const submissionStatus: AnswerSubmissionStatus = submissionPending
    ? 'saving'
    : syncStatus.status !== 'idle'
      ? syncStatus.status
      : lastSubmission?.queued
        ? isOffline
          ? 'saved-locally-offline'
          : 'saved-locally-syncing'
        : questionSubmission.isSuccess
          ? 'saved'
          : 'idle';
  // Kept on the device, the answer is recorded as saved, and the status line says
  // where it is. Until the server has it, the answer carries no "Saved" tag (#174).
  const isAnswerKept =
    submissionStatus === 'saved-locally-offline' ||
    submissionStatus === 'saved-locally-syncing';
  const isStoredStatus = submissionStatus === 'saved' || isAnswerKept;
  const shownStatus: AnswerSubmissionStatus =
    isEntryUnsaved && isStoredStatus ? 'idle' : submissionStatus;
  const isCheckSubmission = questionSubmission.variables?.mode === 'check';
  // A check reads as a check: "Saving" beside the answer's own "Saved" tag
  // said two things at once (HW-04).
  const submissionStatusLabel =
    isCheckSubmission && shownStatus === 'saving'
      ? 'Checking'
      : isCheckSubmission && shownStatus === 'saved'
        ? 'Checked'
        : getAnswerSubmissionStatusLabel(shownStatus);

  // A later due date opens the section again, and what the card said of the old one
  // goes with it. One that leaves it closed does not: the read after a refusal can
  // bring an earlier due date (an instructor's Close now), and nothing else on the
  // page then says what became of the answer.
  useEffect(() => {
    if (isSectionOpen) {
      setClosedNotice(null);
    }
  }, [closesAtUtcMilli, isSectionOpen]);

  useEffect(() => {
    if (!isOffline) {
      setOfflineNotice(null);
    }
  }, [isOffline]);

  useEffect(() => {
    if (isKeptCheckSent && lastSubmission) {
      setAwaitedVerdictFrom(lastSubmission.attemptsBefore);
    }
    // Once, when the report comes: the verdict's own effect below ends the wait.
  }, [isKeptCheckSent]);

  useEffect(() => {
    if (!refusedRequestId || !lastSubmission || syncStatus.reason !== 'section-closed') {
      return;
    }

    const notice = getClosedSubmissionMessage(
      closesAtUtcMilli,
      lastSubmission.mode,
      submissionContext.sectionKind,
    );
    setClosedNotice(notice);
    onAnnounce?.(notice);
    onRefusedAfterClose?.();
    // Once, when the report comes. A page hears each report twice.
  }, [refusedRequestId]);

  // The verdict of a check arrives with the chapter's refetch, after the check
  // resolves. It is announced when the page shows it, so a screen reader hears
  // what is on screen; a refetch that brings none leaves "checked" alone. An
  // upload's grade waits for the close, so it is announced with no verdict.
  useEffect(() => {
    if (awaitedVerdictFrom === null) {
      return undefined;
    }

    const isUpload = question.kind === 'aiUpload';
    const verdict =
      question.feedback && !isUpload
        ? verdictWords[question.feedback.status]
        : undefined;

    if (question.attemptsUsed > awaitedVerdictFrom && (verdict || isUpload)) {
      onAnnounce?.(
        verdict
          ? `${question.label} checked: ${verdict}.`
          : `${question.label} checked.`,
      );
      setAwaitedVerdictFrom(null);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      onAnnounce?.(`${question.label} checked.`);
      setAwaitedVerdictFrom(null);
    }, VERDICT_WAIT_MS);

    return () => window.clearTimeout(timer);
  }, [
    awaitedVerdictFrom,
    onAnnounce,
    question.attemptsUsed,
    question.feedback,
    question.kind,
    question.label,
  ]);
  const earnedPoints =
    question.feedback?.earnedPoints ?? question.scoreEarned;
  // Use the feedback's own possible total when present: under hybrid
  // participation weight the achievable max exceeds pointsPossible, and a wrong
  // answer earning only the participation award must NOT lock as "max earned"
  // (see MultipleChoiceQuestion for the same rule).
  const maxPointsThreshold =
    question.feedback?.possiblePoints ?? question.pointsPossible;
  // An upload's grade waits for the close, and a lock would give it away: only
  // its attempts end its submissions (issue #169).
  const hasEarnedMaxPoints =
    question.kind !== 'aiUpload' &&
    earnedPoints !== undefined &&
    maxPointsThreshold > 0 &&
    earnedPoints >= maxPointsThreshold;
  const interaction = useQuestionInteractionState({
    attemptsAllowed: question.maxAttempts,
    attemptsUsed: question.attemptsUsed,
    areSolutionsVisible,
    hasEarnedMaxPoints,
    hasCheckedAtLeastOnce:
      question.attemptsUsed > 0 && question.feedback?.status !== 'saved',
    hasSavedAnswer: true,
    hideStatsAndSolutions: hideStats,
    hintSetting: question.hintSetting,
    isPractice: !isAssigned || !question.isAssigned,
    isSectionClosed,
    isSectionOpen,
    isSingleAttemptMode: question.isSingleAttemptMode,
    role,
  });
  const showsLockedMessage =
    isAssigned && role === 'student' && interaction.isAnswerLocked && !isSectionClosed;

  // Whether the last focus on the page was on this question's Save or Check
  // (AnswerActionButton). A press that locks the question hands focus on to
  // the message saying so: from a button left unavailable, or from one the
  // lock took away, whose focus fell to the page with no focus event saying
  // so (issue #114). A multi-part question's buttons are its parts'.
  const hadActionFocusRef = useRef(false);
  const lockedMessageRef = useRef<HTMLParagraphElement>(null);
  const isAnswerable =
    isAssigned && role === 'student' && isSectionOpen && question.kind !== 'multiPart';

  useEffect(() => {
    if (!isAnswerable) {
      return undefined;
    }

    function recordFocus(event: FocusEvent) {
      hadActionFocusRef.current =
        event.target instanceof HTMLElement &&
        event.target.dataset.answerAction === question.id;
    }

    document.addEventListener('focusin', recordFocus);

    return () => document.removeEventListener('focusin', recordFocus);
  }, [isAnswerable, question.id]);

  // A press that fails brings the message saying so into view, by as little as
  // it takes, and leaves focus on the button. Pressed at the foot of the
  // window, Save put its message below the fold, where nothing on the screen
  // said the answer was not saved (#172). Set by each press until it is known
  // not to have failed: a message shown without one, such as a refusal
  // reported later for an answer kept offline, moves nothing.
  const bringsFailureIntoViewRef = useRef(false);
  const failureMessageRef = useCallback((message: HTMLParagraphElement | null) => {
    if (message && bringsFailureIntoViewRef.current) {
      bringsFailureIntoViewRef.current = false;
      message.scrollIntoView({ block: 'nearest' });
    }
  }, []);

  useEffect(() => {
    if (!showsLockedMessage || !hadActionFocusRef.current) {
      return;
    }

    const focused = document.activeElement;
    const isOnAction =
      focused instanceof HTMLElement && focused.dataset.answerAction === question.id;
    const isFocusLost =
      focused === null || focused === document.body || !focused.isConnected;

    if (isOnAction || isFocusLost) {
      lockedMessageRef.current?.focus();
    }
  }, [question.id, showsLockedMessage]);

  async function submitAnswer(
    answer: NormalizedAnswerInput,
    mode: 'check' | 'save',
  ) {
    bringsFailureIntoViewRef.current = true;

    // A page left open past the close still showed Check and sent the answer
    // (issue #92, POST-02). Past the close by this page's clock, nothing is
    // sent: the card says so, the section reads the chapter again, and the
    // question records nothing, as for any submission that was not stored.
    if (closesAtUtcMilli !== undefined && Date.now() >= closesAtUtcMilli) {
      const notice = getClosedSubmissionMessage(
        closesAtUtcMilli,
        mode,
        submissionContext.sectionKind,
      );
      setClosedNotice(notice);
      onAnnounce?.(notice);
      onClosed?.();
      throw new Error(notice);
    }

    setClosedNotice(null);
    setOfflineNotice(null);

    if (mode === 'check' && isOffline) {
      const notice = getOfflineCheckMessage(question.kind, isEntryUnsaved);
      setOfflineNotice(notice);
      onAnnounce?.(notice);
      throw new Error(notice);
    }

    const attemptsBeforeSubmission = question.attemptsUsed;
    let wasCheckKept = false;

    try {
      const result = await questionSubmission.mutateAsync({
        answer,
        componentId,
        courseId,
        mode,
        parentQuestionId: question.parentQuestionId,
        questionId: question.id,
        sectionId: question.sectionId,
        ...submissionContext,
      });
      bringsFailureIntoViewRef.current = false;
      const submissionResult = result ?? { queued: false, requestId: '' };
      setLastSubmission(
        submissionResult.requestId.length > 0
          ? { ...submissionResult, attemptsBefore: attemptsBeforeSubmission, mode }
          : null,
      );

      if (mode === 'check') {
        if (submissionResult.queued) {
          wasCheckKept = true;
        } else {
          // Announced with its verdict once the page shows it (see above).
          setAwaitedVerdictFrom(attemptsBeforeSubmission);
          return;
        }
      } else {
        onAnnounce?.(
          submissionResult.queued
            ? `${question.label} saved locally and will sync.`
            : `${question.label} saved.`,
        );
      }
    } catch (error) {
      if (isSectionClosedRefusal(error)) {
        const notice = getClosedSubmissionMessage(
          closesAtUtcMilli,
          mode,
          submissionContext.sectionKind,
        );
        setClosedNotice(notice);
        setLastSubmission(null);
        onAnnounce?.(notice);
        onRefusedAfterClose?.();
        throw error;
      }

      // Never announce raw error text — backend failures carry internal
      // error strings in `message`.
      onAnnounce?.(getSubmissionErrorMessage(error, mode));
      // And tell the question: it records an answer as saved or checked only
      // when this resolves. Returning normally here is what once put a "Saved"
      // badge on an answer the server had not stored.
      throw error;
    }

    if (wasCheckKept) {
      const message = getKeptCheckMessage(question.kind);
      onAnnounce?.(message);

      // A file is different: it has no verdict to get wrong, and left ready to
      // submit, a press would use another attempt on it.
      if (question.kind === 'aiUpload') {
        return;
      }

      // Not checked yet, so the question must not record it as checked: that
      // put the verdict of the last check beside this answer (issue #143).
      throw new Error(message);
    }
  }

  const body =
    question.kind === 'aiUpload' ? (
      // Only a student hands in a file. An assistant reads the question as an
      // instructor does, as every other answer is read-only for them (issue #170).
      role !== 'student' ? (
        <AIUploadInstructorView question={question} />
      ) : (
        <AIUploadStudentView
          isSectionOpen={isSectionOpen}
          onSubmit={(answer) => submitAnswer(answer, 'check')}
          pending={isAwaitingSubmission}
          question={question}
          refusedSubmissionId={refusedSubmissionId}
          showFeedback={interaction.showSolution}
          showScore={interaction.showEarnedPoints}
        />
      )
    ) : question.kind === 'multipleChoice' ? (
      <MultipleChoiceQuestion
        areSolutionsVisible={areSolutionsVisible}
        hideStats={hideStats}
        isAnswerKept={isAnswerKept}
        isSectionClosed={isSectionClosed}
        isSectionOpen={isSectionOpen}
        onCheck={(answer) => submitAnswer(answer, 'check')}
        onEntryChange={setIsEntryUnsaved}
        onSave={(answer) => submitAnswer(answer, 'save')}
        pending={isAwaitingSubmission}
        question={question}
        refusedSubmissionId={refusedSubmissionId}
        role={role}
        showActions={isAssigned}
      />
    ) : question.kind === 'numerical' ? (
      <NumericalQuestion
        areSolutionsVisible={areSolutionsVisible}
        hideStats={hideStats}
        isAnswerKept={isAnswerKept}
        isSectionClosed={isSectionClosed}
        isSectionOpen={isSectionOpen}
        onCheck={(answer) => submitAnswer(answer, 'check')}
        onEntryChange={setIsEntryUnsaved}
        onSave={(answer) => submitAnswer(answer, 'save')}
        pending={isAwaitingSubmission}
        question={question}
        refusedSubmissionId={refusedSubmissionId}
        role={role}
        showActions={isAssigned}
      />
    ) : question.kind === 'exactAnswer' ? (
      <ExactAnswerQuestion
        areSolutionsVisible={areSolutionsVisible}
        hideStats={hideStats}
        isAnswerKept={isAnswerKept}
        isSectionClosed={isSectionClosed}
        isSectionOpen={isSectionOpen}
        onCheck={(answer) => submitAnswer(answer, 'check')}
        onEntryChange={setIsEntryUnsaved}
        onSave={(answer) => submitAnswer(answer, 'save')}
        pending={isAwaitingSubmission}
        question={question}
        refusedSubmissionId={refusedSubmissionId}
        role={role}
        showActions={isAssigned}
      />
    ) : question.kind === 'selectAll' ? (
      <SelectAllQuestion
        areSolutionsVisible={areSolutionsVisible}
        hideStats={hideStats}
        isAnswerKept={isAnswerKept}
        isSectionClosed={isSectionClosed}
        isSectionOpen={isSectionOpen}
        onCheck={(answer) => submitAnswer(answer, 'check')}
        onEntryChange={setIsEntryUnsaved}
        onSave={(answer) => submitAnswer(answer, 'save')}
        pending={isAwaitingSubmission}
        question={question}
        refusedSubmissionId={refusedSubmissionId}
        role={role}
        showActions={isAssigned}
      />
    ) : (
      <MultiPartQuestion>
        {question.parts.map((part) => (
          <QuestionCard
            areSolutionsVisible={areSolutionsVisible}
            closesAtUtcMilli={closesAtUtcMilli}
            componentId={componentId}
            courseId={courseId}
            aiContext={aiContext}
            headingLevel={4}
            hideStats={hideStats}
            isAssigned={isAssigned}
            isSectionClosed={isSectionClosed}
            isSectionOpen={isSectionOpen}
            key={part.id}
            onAnnounce={onAnnounce}
            onClosed={onClosed}
            onRefusedAfterClose={onRefusedAfterClose}
            question={part}
            role={role}
            submissionContext={submissionContext}
          />
        ))}
      </MultiPartQuestion>
    );

  const showReviewFeedback =
    isAssigned &&
    role === 'student' &&
    isSectionClosed &&
    interaction.showCorrectIncorrectFeedback &&
    question.feedback?.status !== 'saved';
  const showQuestionStats =
    isAssigned &&
    (interaction.showEarnedPoints ||
      interaction.showCorrectAnswer ||
      (question.isInstructorView && !hideStats) ||
      (role === 'student' &&
        isSectionClosed &&
        question.solutionsAvailableAtUtcMilli !== undefined));
  const hintDisclosure = question.hint
    ? {
        ...question.hint,
        isVisible: interaction.showHints,
      }
    : undefined;
  const solutionDisclosure = question.solution
    ? {
        ...question.solution,
        isVisible: interaction.showSolution,
      }
    : undefined;
  // The section is on the page for this student when it is open OR closed --
  // ContentPage renders question sections on exactly that condition. AI help
  // stays available after the due date; only submissions end when a section
  // closes.
  const isSectionAvailable = isSectionOpen || isSectionClosed;
  // Hints and practice problems are independent settings, so the AI panel mounts
  // when EITHER is on. Gating the whole panel on allowAIHints was the last place
  // that kept practice problems tied to the hint flag.
  const canUseAIHomework =
    Boolean(aiContext) &&
    isAssigned &&
    question.isAssigned &&
    (question.allowAIHints === true || question.allowAIPracticeProblems === true) &&
    question.kind !== 'multiPart' &&
    question.kind !== 'aiUpload' &&
    (role === 'student' || role === 'instructor') &&
    aiContext?.sectionKind === 'homework' &&
    isSectionAvailable;
  const staticInfoPanelTabs = useInfoPanelTabs({
    hintDisclosure,
    solutionDisclosure,
  });

  function renderStaticInfoPanelContent(tabId: QuestionInfoPanelTabId) {
    if (tabId === 'hint') {
      return (
        <HintTabContent
          aiHint={emptyHintState}
          builtInHint={hintDisclosure}
          onRequestAIHint={() => {}}
          onVoteAIHint={() => {}}
          showAIHintButton={false}
        />
      );
    }

    return <SolutionTabContent solution={solutionDisclosure} />;
  }

  return (
    <Card
      as="article"
      className={styles.card}
      data-assigned={isAssigned ? 'true' : 'false'}
      data-locked={interaction.isAnswerLocked ? 'true' : 'false'}
      variant="standard"
    >
      <div className={styles.header}>
        <HeadingTag className={styles.title}>{question.label}</HeadingTag>
        <div className={styles.meta}>
          {!isAssigned ? (
            <p className={styles.practiceTag}>Practice question</p>
          ) : null}
          {/* Staff only. An instructor otherwise had no sign that a question was
              set to participation credit outside the edit dialog's checkbox, so
              a setting that HAD saved looked like it had not. Students are told
              once they answer, in the feedback; announcing it beforehand only
              invites them to submit anything. */}
          {role !== 'student' && question.isParticipationOnly ? (
            <p className={styles.participationTag}>Participation credit</p>
          ) : null}
          <p className={styles.points}>{getPointsLabel(question.pointsPossible)}</p>
          {/* After the close, a question with no answer at all scores 0, as
              the section total counts it; one with an answer but no score yet
              (an upload waiting to be graded) shows none. */}
          {isAssigned &&
          interaction.showEarnedPoints &&
          (question.scoreEarned !== undefined || question.hasResponse === false) ? (
            <p className={styles.points}>
              Score: {question.scoreEarned ?? 0}/{question.pointsPossible}
            </p>
          ) : null}
          {isAssigned && interaction.showAttemptCount ? (
            <AttemptTracker attemptsLabel={question.attemptsLabel} />
          ) : null}
        </div>
      </div>

      {question.promptUnits.length > 0 ? (
        <QuestionContent
          headingLevel={headingLevel === 3 ? 4 : 4}
          role={role}
          units={question.promptUnits}
        />
      ) : null}

      {question.usesRepresentativeVariableValues ? (
        <p className={styles.representativeValuesNote} role="note">
          Representative values shown. Each student sees their own randomized
          numbers.
        </p>
      ) : null}

      {question.imagePaths && question.imagePaths.length > 0 ? (
        <div className={styles.imageAttachments}>
          {question.imagePaths.map((imagePath, index) => (
            <img
              alt={`${question.label} image ${index + 1}`}
              className={styles.imageAttachment}
              key={`${imagePath}-${index}`}
              loading="lazy"
              src={imagePath}
            />
          ))}
        </div>
      ) : null}

      {showsLockedMessage ? (
        <p
          className={styles.lockedMessage}
          data-reason={interaction.isLockedForCorrectAnswer ? 'correct' : 'attempts'}
          ref={lockedMessageRef}
          tabIndex={-1}
        >
          {interaction.isLockedForCorrectAnswer
            ? 'You answered this correctly, so this question is complete — no more submissions are allowed.'
            : 'No attempts remain for this question.'}
        </p>
      ) : null}

      {body}

      {isAssigned && submissionStatusLabel ? (
        <p
          aria-live="polite"
          className={styles.submissionStatus}
          data-status={shownStatus}
          role="status"
        >
          {submissionStatusLabel}
        </p>
      ) : null}

      {canUseAIHomework && aiContext ? (
        <QuestionAIInfoPanel
          aiContext={aiContext}
          componentId={componentId}
          hintDisclosure={hintDisclosure}
          hintsHidden={hideStats}
          isSectionAvailable={isSectionAvailable}
          question={question}
          role={role}
          solutionDisclosure={solutionDisclosure}
        />
      ) : staticInfoPanelTabs.length > 0 ? (
        <QuestionInfoPanel
          renderContent={renderStaticInfoPanelContent}
          tabs={staticInfoPanelTabs}
        />
      ) : null}

      {showQuestionStats ? (
        <QuestionStatsDisplay
          hideStats={hideStats}
          question={question}
          showCorrectAnswer={interaction.showCorrectAnswer}
          showEarnedPoints={interaction.showEarnedPoints}
          showFeedback={showReviewFeedback}
          showStudentAnswer={role === 'student' && isAssigned && isSectionClosed}
          showStats={Boolean(question.isInstructorView && !hideStats)}
        />
      ) : null}

      {isAssigned && closedNotice ? (
        <p className={styles.errorMessage} ref={failureMessageRef} role="alert">
          {closedNotice}
        </p>
      ) : isAssigned && offlineNotice ? (
        <p className={styles.errorMessage} ref={failureMessageRef} role="alert">
          {offlineNotice}
        </p>
      ) : isCheckKept ? (
        <p className={styles.keptCheckMessage} role="status">
          {getKeptCheckMessage(question.kind)}
        </p>
      ) : isAssigned &&
        questionSubmission.isError &&
        !isSectionClosedRefusal(questionSubmission.error) ? (
        <p className={styles.errorMessage} ref={failureMessageRef} role="alert">
          {getSubmissionErrorMessage(
            questionSubmission.error,
            questionSubmission.variables?.mode,
          )}
        </p>
      ) : null}
    </Card>
  );
});
